
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { defaultMetaTags, routeMetaTags } from "./metaConfig";

type MetaTagsProps = {
  customTitle?: string;
  customDescription?: string;
  customImage?: string;
  customUrl?: string;
};

/**
 * Component for dynamically updating meta tags based on current route
 * Can also accept custom props to override the route-based configuration
 */
const MetaTags = ({ 
  customTitle, 
  customDescription, 
  customImage,
  customUrl 
}: MetaTagsProps) => {
  const location = useLocation();
  
  useEffect(() => {
    // Get meta configuration for current route, or fall back to default
    const routeConfig = routeMetaTags[location.pathname] || defaultMetaTags;
    
    // Use custom values if provided, otherwise use route config
    const title = customTitle || routeConfig.title;
    const description = customDescription || routeConfig.description;
    const image = customImage || routeConfig.ogImage || defaultMetaTags.ogImage;
    const url = customUrl || routeConfig.ogUrl || `https://metrade.io${location.pathname}`;
    const twitterCard = routeConfig.twitterCard || defaultMetaTags.twitterCard;
    
    // Update document title
    document.title = title;
    
    // Update meta tags
    updateMetaTag("description", description);
    
    // Update Open Graph tags
    updateMetaTag("og:title", title);
    updateMetaTag("og:description", description);
    updateMetaTag("og:url", url);
    if (image) {
      updateMetaTag("og:image", image);
      // Add image dimensions for better social media rendering
      updateMetaTag("og:image:width", "1200");
      updateMetaTag("og:image:height", "630");
    }
    updateMetaTag("og:type", "website");
    
    // Update Twitter Card tags
    updateMetaTag("twitter:card", twitterCard || "summary_large_image");
    updateMetaTag("twitter:title", title);
    updateMetaTag("twitter:description", description);
    if (image) updateMetaTag("twitter:image", image);
    
  }, [location.pathname, customTitle, customDescription, customImage, customUrl]);
  
  // Helper function to update meta tags
  const updateMetaTag = (name: string, content: string) => {
    // Check if tag already exists
    let metaTag: HTMLMetaElement | null;
    
    if (name.startsWith("og:")) {
      metaTag = document.querySelector(`meta[property="${name}"]`);
      if (!metaTag) {
        metaTag = document.createElement("meta");
        metaTag.setAttribute("property", name);
        document.head.appendChild(metaTag);
      }
    } else {
      metaTag = document.querySelector(`meta[name="${name}"]`);
      if (!metaTag) {
        metaTag = document.createElement("meta");
        metaTag.setAttribute("name", name);
        document.head.appendChild(metaTag);
      }
    }
    
    metaTag.setAttribute("content", content);
  };
  
  // This component doesn't render anything visible
  return null;
};

export default MetaTags;
